import { useMutation } from 'react-query';
import {
  checkCoverageService,
  createHomeownerLeadService,
  trackPersonalDetailsService,
} from 'src/api/homeowner-lead';
import {
  createOnboardingPropertiesService,
  createXPropertyService,
  fetchXHomesService,
} from 'src/api/homeowner-onboarding';
import {
  fetchHomeownerFlowsService,
  fetchHomeownerPropertiesService,
} from 'src/api/homeowner-properties';
import { HomeownerLead, NewOwnerOnboardingModel } from 'src/api/models';
import { CreateHomeCommand } from 'src/api/octane/models.homes';
import { getRegionById } from 'src/constants/address';
import {
  GA_ACTIONS,
  trackHomeownerLeadCreated,
  trackHomeownerXLeadCreated,
} from 'src/utils/tracking';
import useSWR from 'swr';
export function useTrackPersonalDetailsMutation() {
  const { isLoading, mutateAsync } = useMutation(trackPersonalDetailsService);
  return {
    isTrackPersonalDetailsLoading: isLoading,
    trackPersonalDetails: mutateAsync,
  };
}

export function useCheckCoverageMutation() {
  const { isLoading, mutateAsync } = useMutation(checkCoverageService);
  return { isCheckCoverageLoading: isLoading, checkCoverage: mutateAsync };
}

export function useCreateHomeownerLeadMutation() {
  const { isLoading, mutateAsync } = useMutation(createHomeownerLeadService);
  return {
    isCreatingHomeownerLead: isLoading,
    createHomeownerLead: mutateAsync,
  };
}

const sourceActionMap: Partial<
  Record<HomeownerLead['creationSource'], string>
> = {
  SignUpFlow: GA_ACTIONS.LEAD_CREATED_SIGNUP_FLOW,
  Calculator: GA_ACTIONS.LEAD_CREATED_CALCULATOR,
  Leasing: GA_ACTIONS.LEAD_CREATED_CALCULATOR,
  RentEstimate: GA_ACTIONS.LEAD_CREATED_RENT_ESTIMATE,
};

export async function createProperties(
  ownerOnboardingModel: NewOwnerOnboardingModel,
) {
  const onboardingProperties =
    await createOnboardingPropertiesService(ownerOnboardingModel);

  try {
    const action = sourceActionMap[ownerOnboardingModel.creationSource];
    onboardingProperties?.properties?.forEach((property) => {
      const { units, address, basicInfo } = property;
      units?.forEach((unit) => {
        let coverageType = 'out_of_coverage';
        let region;
        if (basicInfo?.inCoverageArea) {
          region = getRegionById(basicInfo?.coverageRegionId || -1);
          if (basicInfo.isInPrimaryCoverageArea) {
            coverageType = 'primary_zipcode';
          } else {
            coverageType = 'secondary_zipcode';
          }
        }

        trackHomeownerLeadCreated({
          action,
          address,
          rent: unit.estimatedPrice?.rent,
          coverageType,
          region: region?.slug,
        });
      });
    });
  } catch (e) {
    console.error(e);
  }

  return onboardingProperties;
}

async function createXProperties({
  xProperty,
  source,
}: {
  xProperty: CreateHomeCommand;
  source: string;
}) {
  try {
    const homeCreated = await createXPropertyService(xProperty);
    trackHomeownerXLeadCreated(homeCreated.address, source);

    return homeCreated;
  } catch (error) {
    console.error(error);
  }
}

export function useXHomes() {
  const {
    data,
    error: homesError,
    mutate: mutateHomes,
    ...rest
  } = useSWR('/homeowners/homes-x', (_key: string) => fetchXHomesService());
  const homes = data?.items || [];

  return {
    homes,
    homesError,
    isHomesLoading: !homes && !homesError,
    mutateHomes,
    ...rest,
  };
}

export function useCreateProperties() {
  const { isLoading, mutateAsync } = useMutation(createProperties);
  return {
    isCreatePropertiesLoading: isLoading,
    createProperties: mutateAsync,
  };
}

export function useCreateBelongXProperty() {
  const { isLoading, mutateAsync } = useMutation(createXProperties);
  return {
    isCreateXPropertiesLoading: isLoading,
    createXProperties: mutateAsync,
  };
}

export function useHomeownerFlows() {
  const {
    data: flows,
    error: flowsError,
    mutate: mutateFlows,
    ...rest
  } = useSWR('/homeowners/flows', (_key: string) =>
    fetchHomeownerFlowsService(),
  );

  return {
    flows,
    flowsError,
    isFlowsLoading: !flows && !flowsError,
    mutateFlows,
    ...rest,
  };
}

type UseHomeownerPropertiesProps = {
  enabled?: boolean;
};

export function useHomeownerProperties({
  enabled,
}: UseHomeownerPropertiesProps = {}) {
  const { data: homeownerProperties } = useSWR(
    enabled ? '/homeowners/properties' : null,
    () => fetchHomeownerPropertiesService(),
  );

  return {
    homeownerProperties,
  };
}
